import $ from 'jquery';
import {startLoadingIndicator, stopLoadingIndicator} from './loading';
import {
    PRODUCT_TYPE_PERCENT_FROM_GROSS,
    PRODUCT_TYPE_PERCENT_FROM_NET,
    PRODUCT_TYPE_PERCENT_LODGING_GROSS,
    PRODUCT_TYPE_PERCENT_LODGING_NET,
    PRODUCT_TYPE_PERCENT_PACKPRICE_GROSS,
    PRODUCT_TYPE_PERCENT_PACKPRICE_NET,
    PRODUCT_TYPE_TIERED_LODGING_GROSS_PER_PERSON_NIGHT,
    PRODUCT_TYPE_TIERED_LODGING_NET_PER_PERSON_NIGHT,
    SYSTEM_DATE_FORMAT,
} from './constants';

export function decimalFormat(value, decimals) {
    return numberFormat(Number(value), decimals, 1, window.config.basicSettings.default_numeral_format);
}

export function currencyFormat(value) {
    return window.config.basicSettings.setting_site_currency + ' ' + decimalFormat(value, 2);
}

export function percentFormat(value, decimals = 2) {
    return `${decimalFormat(value * 100, decimals)} %`;
}

export function dateFormat(value) {
    return moment(value).format(window.config.basicSettings.setting_default_dateformat.toUpperCase());
}

export function dateShortFormat(value) {
    let format = window.config.basicSettings.setting_default_dateformat === 'yyyy/mm/dd' ? 'mm/dd' : 'dd.mm';
    return moment(value).format(format.toUpperCase());
}

export function userDateToSystemDate(value) {
    return moment(value, window.config.basicSettings.setting_default_dateformat.toUpperCase(), true).format(SYSTEM_DATE_FORMAT);
}

export function datetimeFormat(value) {
    return moment(value).format(window.config.basicSettings.setting_default_dateformat.toUpperCase() + ' HH:mm');
}
export function datetimeShortFormat(value) {
    let format = window.config.basicSettings.setting_default_dateformat === 'yyyy/mm/dd' ? 'mm/dd' : 'dd.mm';
    format = format.toUpperCase() + ' HH:mm';
    return moment(value).format(format);
}

export function applyTimezone(value) {
    return moment(value).tz(window.config.basicSettings.setting_country_time_zone);
}

export function durationFormat(value) {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value - hours * 3600) / 60);
    const seconds = value - hours * 3600 - minutes * 60;

    let result = (hours !== 0 ? `${hours}h` : '') + (minutes !== 0 ? ` ${minutes}m` : '') + (seconds !== 0 ? ` ${seconds}s` : '');

    // Adds spaces after hours and minutes if followed by a number
    return result.trim();
}

/**
 * Y-m-d format, ignoring timezone
 */
export function dateYMD(date) {
    const year = date.getFullYear().toString().padStart(4, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // 0-index
    const day = (date.getDate()).toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Checks if the provided timestamps is after the page initially loaded.
 *
 * Main use case is to check if effects from received event already are handled in UI.
 *
 * @param {number} timestamp
 * @param {number} tolerance
 */
export function isAfterLoad(timestamp, tolerance = 5) {
    // window.navigationStart is set during page load
    return window.navigationStart.getTime() / 1000 - tolerance <= timestamp;
}

/**
 * @param {string} csvContent
 * @param {string} filename
 */
export function triggerCsvFileDownload(csvContent, filename) {
    const url = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv' }));

    const downloadTrigger = document.createElement('a');
    downloadTrigger.href = url;
    downloadTrigger.download = filename;
    document.body.appendChild(downloadTrigger);
    downloadTrigger.click();
    document.body.removeChild(downloadTrigger);
    URL.revokeObjectURL(url);
}

export function translate(category, message, params = {}) {
    return translateRaw(category, message, params).join('');
}

/**
 * Similar to `translate` but gives the raw tokens instead of a plain string.
 *
 * Examples:
 *
 * input: '{username} created damage "{title}"'
 * output: ['{username}', ' created damage "', '{title}', '"']
 *
 * input: 'Create password'
 * output ['Create password']
 */
export function translateRaw(category, message, params = {}) {
    if (window.hasOwnProperty('translations') && window.translations.hasOwnProperty(category) && window.translations[category].hasOwnProperty(message)) {
        message = window.translations[category][message];
    }

    return tokenizeTranslation(message)
        .map((token) => Array.isArray(token) ? parseToken(token, params) : token);
}

/**
 * Tokenizes a pattern by separating normal text from replaceable patterns.
 *
 * Examples:
 *
 * input: '{username} created damage "{title}"'
 * output: [['username'], 'created damage "', ['title'], '"']
 *
 * input: 'Create password'
 * output ['Create password']
 *
 * input: '{username} {newStatus,select,IN_PROGRESS{started resolving} COMPLETED{resolved}} damage "{title}"'
 * output: [['username'], ['newStatus', 'select', 'IN_PROGRESS{started resolving} COMPLETED{resolved}'], 'damage "', ['title'], '"']
 *
 * input: '{count,plural,=1{# possible duplicate} other{# possible duplicates}} found for this client'
 * output: ['', ['count', 'plural', '=1{# possible duplicate} other{# possible duplicates}'], ' found for this client],
 */
function tokenizeTranslation(message) {
    let pos = message.indexOf('{');
    let start = pos;
    if (pos === -1) {
        // No tokens are present
        return [message];
    }

    let depth = 1;
    const tokens = [
        message.substr(0, pos),
    ];
    while (true) {
        let open = message.indexOf('{', pos + 1);
        let close = message.indexOf('}', pos + 1);
        if (open === -1 && close === -1) {
            break;
        }
        if (open === -1) {
            open = message.length;
        }
        if (close > open) {
            ++depth;
            pos = open;
        } else {
            --depth;
            pos = close;
        }
        if (depth === 0) {
            tokens.push(
                message.substr(start + 1, pos - start - 1).split(',', 3),
            );
            start = pos + 1;
            tokens.push(
                message.substr(start, open - start),
            );
            start = open;
        }
        if (depth !== 0 && (open === -1 || close === -1)) {
            break;
        }
    }

    return tokens;
}

function parseToken(token, params) {
    const [param, type, args] = token;
    if (!params.hasOwnProperty(param)) {
        return '{' + token.join(',') + '}';
    }
    const arg = params[param];

    if (type === 'select') {
        const select = tokenizeTranslation(args);
        for (let i = 0; i < select.length - 1; i += 2) {
            if (Array.isArray(select[i]) || !Array.isArray(select[i + 1])) {
                // Wrong format. Return as is
                return '{' + token.join(',') + '}';
            }
            const selector = select[i].trim();
            if (selector === arg || selector === 'other') {
                return select[i + 1];
            }
        }
    } else if (type === 'plural') {
        const plural = tokenizeTranslation(args);
        let otherFallback = null;
        for (let i = 0; i < plural.length - 1; i += 2) {
            if (Array.isArray(plural[i]) || !Array.isArray(plural[i + 1])) {
                // Wrong format. Return as is
                return '{' + token.join(',') + '}';
            }

            if (plural[i].trim() === 'other') {
                // `other` might be defined before the actual value
                // other{# dogs} =1{# dog}
                otherFallback = plural[i + 1];
            } else {
                // noinspection EqualityComparisonWithCoercionJS Allow comparing '1' to 1
                if (plural[i].trim().match(/^=(?<value>\d+)$/).groups?.value == arg) {
                    return plural[i + 1][0].replaceAll('#', arg);
                }
            }
            // ICU format also supports `few` and `many` selectors, but they're language dependant, and not really used. Treat them as naturally dead.
        }

        if (otherFallback !== null) {
            return otherFallback[0].replaceAll('#', arg);
        }
    } else {
        return arg;
    }

    return '{' + token.join(',') + '}';
}

/**
 * Returns a number whose value is limited to the given range.
 *
 * Example: limit the output of this computation to between 0 and 255
 * (x * 255).clamp(0, 255)
 *
 * @param {Number} value
 * @param {Number} min The lower boundary of the output range
 * @param {Number} max The upper boundary of the output range
 * @returns A number in the range [min, max]
 */
export function clamp(value, min, max) {
    return Math.min(Math.max(value || 0, min), max);
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
// https://davidwalsh.name/javascript-debounce-function
export function debounce(func, wait, immediate) {
    var timeout;
    return function() {
        var context = this, args = arguments;
        var later = function() {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

export function handleXhrError(error) {
    if (error.status === 302) {
        return;
    }

    console.error(error);
    if (error.status === 0) {
        alert(translate('frontend', 'NETWORK_ISSUE_ERROR'));
    } else if (error.responseJSON) {
        alert(`${error.status}: ${error.responseJSON.name}\n${error.responseJSON.message}`);
    } else {
        alert(`${error.status}: ${error.responseText}`);
    }
}

export async function handleFetchError(response) {
    let message = translate('frontend', 'ERROR_INPROCESS');
    if (response.headers.get('Content-Type').indexOf('application/json') !== -1) {
        message = (await response.json()).message;
    }
    alert(`${response.status}: ${message}`);
}

/**
 * @param {string | number} country Country code or country id
 * @param {Object} [options]
 * @param {boolean} [options.asMap] - If true, resolve with raw response object { [regionId]: regionName }
 * @returns {Promise<({ optionsHtml: string } | Object<string, string>)>}
 */
export function getCountryRegions(country, options) {
    return new Promise((resolve, reject) => {
        $.ajax({
            type: 'GET',
            url: '/country-region/index',
            data: {country},
            dataType: 'json',
            beforeSend: () => startLoadingIndicator(),
            complete: () => stopLoadingIndicator(),
            success: (response) => {
                if (options?.asMap) {
                    resolve(response);
                    return;
                }

                let optionsHtml = `<option value="">${translate('frontend', 'SELECT_REGION')}</option>`;

                for (const [id, name] of Object.entries(response)) {
                    const option = document.createElement('option');
                    option.value = id;
                    option.textContent = name + '';

                    optionsHtml += option.outerHTML;
                }

                resolve(optionsHtml);
            },
            error: (error) => {
                handleXhrError(error);

                reject(error);
            },
        });
    });
}

export function autosaveCategories(selection) {
    let value;
    if (selection === undefined) {
        value = 'clear';
    } else if (selection.length === 0) {
        value = 'empty';
    } else {
        value = selection;
    }

    $.ajax({
        url: '/calendar/autosave',
        type: 'post',
        data: {
            calendarCategories: value,
        },
    });
}

/**
 * @param {Object} errorEntries
 * @param {function} formGroupResolver
 */
export function updateErrors(errorEntries, formGroupResolver) {
    let allErrorsHandled = true;
    for (let [id, errors] of Object.entries(errorEntries)) {
        const formGroup = formGroupResolver(id);
        if (formGroup) {
            formGroup.classList.add('has-error');
            let errorElement = formGroup.querySelector('.help-block');
            if (!errorElement) {
                errorElement = document.createElement('p');
                errorElement.classList.add('help-block');
                errorElement.style.whiteSpace = 'pre-line';
                formGroup.insertAdjacentElement('beforeend', errorElement);
            }

            errorElement.innerText = errors.join('\n');
        } else {
            // Error in a field thats not part of the form
            allErrorsHandled = false;
        }
    }

    return allErrorsHandled;
}

export function monthsList(locale) {
    const months = [];
    for (let monthIdx = 0; monthIdx < 12; ++monthIdx) {
        months.push(
            // use middle of month to avoid timezone issues
            (new Date(2022, monthIdx, 15)).toLocaleDateString(locale, {month: 'long'}),
        );
    }

    return months;
}

export function parseGraphQLError(error, fieldLookup) {
    if (!error.graphQLErrors || !error.graphQLErrors.length) {
        // not a graphql error response
        throw error;
    }

    const unhandledError = error.graphQLErrors.find((error) => {
        // Validation errors are for invalid data - amount too low or such.
        // Configuration is for either a disabled functionality.
        // Both are safe to show to hotel staff.

        const errorCategory = error.extensions?.category;
        return errorCategory !== 'validation' && errorCategory !== 'configuration';
    });

    if (unhandledError) {
        // Some internal error mayhaps?
        throw new Error(unhandledError.message);
    }

    const fieldErrors = {};
    const unknownErrors = [];
    for (const validationError of error.graphQLErrors) {
        let field = null;
        if (validationError.extensions.argumentPath) {
            field = fieldLookup(validationError.extensions.argumentPath);
        }

        if (field !== null && field !== undefined) {
            fieldErrors[field] ??= [];
            fieldErrors[field].push(validationError.message);
        } else {
            unknownErrors.push(validationError.message);
        }
    }

    return [fieldErrors, unknownErrors];
}

export function isPercentUnit(unit) {
    return unit === PRODUCT_TYPE_PERCENT_FROM_NET || unit === PRODUCT_TYPE_PERCENT_FROM_GROSS || unit === PRODUCT_TYPE_PERCENT_LODGING_NET || unit === PRODUCT_TYPE_PERCENT_LODGING_GROSS || unit === PRODUCT_TYPE_PERCENT_PACKPRICE_NET || unit === PRODUCT_TYPE_PERCENT_PACKPRICE_GROSS;
}

export function isTieredUnit(unit) {
    return unit === PRODUCT_TYPE_TIERED_LODGING_GROSS_PER_PERSON_NIGHT || unit === PRODUCT_TYPE_TIERED_LODGING_NET_PER_PERSON_NIGHT;
}

export function enforceDiscountInvariant() {
    const discountElement = document.getElementById('discount');
    const inputElement = document.getElementById('pack-price-disp');

    inputElement.disabled = Number(discountElement.value) === 100;
}

let computedLocalDayNames = {};
/**
 * lengthType = 'narrow' | 'short' | 'long'
 */
export function getLocalDayNames(lengthType, locale) {
    if (computedLocalDayNames[`${lengthType}_${locale}`] !== undefined) {
        return computedLocalDayNames[`${lengthType}_${locale}`]
    }

    let d = new Date(2000,0,2); // Sunday
    let days = [];

    for (let i=0; i<7; i++) {
        days.push(d.toLocaleString(locale,{weekday: lengthType}));
        d.setDate(d.getDate() + 1);
    }

    computedLocalDayNames[`${lengthType}_${locale}`] = days;

    return days;
}

let computedLocalMonthNames = {};
/**
 * lengthType = 'narrow' | 'short' | 'long'
 */
export function getLocalMonthNames(lengthType, locale) {
    if (computedLocalMonthNames[`${lengthType}_${locale}`] !== undefined) {
        return computedLocalMonthNames[`${lengthType}_${locale}`]
    }

    let d = new Date(2000,0); // January
    let months = [];

    for (let i=0; i<12; i++) {
        months.push(d.toLocaleString(locale,{month: lengthType}));
        d.setMonth(i + 1);
    }

    computedLocalMonthNames[`${lengthType}_${locale}`] = months;

    return months;
}

export function rgbToHex(rgb) {
    return `#${rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/).slice(1).map(value => parseInt(value, 10).toString(16).padStart(2, '0')).join('')}`
}

/**
 * Similar to toFixed(...) but without rounding
 *
 * @param {number} number
 * @param {number} precision
 * @return {string}
 */
export function truncateNumber(number, precision) {
    const pattern = new RegExp(`^-?\\d+(?:\\.\\d{0,${precision}})?`);
    return number.toString().match(pattern)[0];
}

/**
 * Similar to setInterval, with the added behaviour that handler will be called  N+1th time only Nth time has resolved,
 * i.e., waits on the previous promise to resolve before calling the handler again.
 *
 * @param {Function} handler
 * @param {number} timeout
 * @param {boolean} immediate Whether to call the handler immediately, or wait for the first time delay
 * @return {Function} A function that, when called, will stop further invocations
 */
export function setWaitingInterval(handler, timeout, immediate = false) {
    let timeoutId = null;
    const timeoutHandler = async () => {
        await handler();
        // Interval might have been cleared while promise was resolving.
        if (timeoutId !== null) {
            timeoutId = setTimeout(timeoutHandler, timeout);
        }
    };

    timeoutId = setTimeout(timeoutHandler, immediate ? 0 : timeout);

    return () => {
        // Cleanup might get called multiple times.
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
}

export function labelColour(backgroundColour) {
    const rgb = backgroundColour.slice(1).match(/.{2}/g);
    const [r, g, b] = rgb.map(c => {
        c = parseInt(c, 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    const luminosity = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    const whiteContrast = (1.0 + 0.05) / (luminosity + 0.05);
    const blackContrast = (luminosity + 0.05) / 0.05;

    return (whiteContrast >= 3.0) || whiteContrast >= blackContrast ? '#ffffff' : '#000000';
}


/**
 * @param {string} unit See ProductUnits enum in graphql api
 * @return {string}
 */
export function productUnitName(unit) {
    if (unit === 'FLAT') {
        return translate('frontend', 'RATE_FLAT');
    } else if (unit === 'PER_PERSON') {
        return translate('frontend', 'RATE_PER_PERSON');
    } else if (unit === 'PER_NIGHT') {
        return translate('frontend', 'RATE_PER_NIGHT');
    } else if (unit === 'PER_DAY') {
        return translate('frontend', 'RATE_PER_DAY');
    } else if (unit === 'PER_ITEM') {
        return translate('frontend', 'RATE_PER_ITEM');
    } else if (unit === 'PER_PERSON_NIGHT') {
        return translate('frontend', 'RATE_PER_PERSON_NIGHT');
    } else if (unit === 'PER_PERSON_DAY') {
        return translate('frontend', 'RATE_PER_PERSON_DAY');
    } else if (unit === 'PERCENT_FROM_NET') {
        return translate('frontend', 'RATE_PERCENT_FROM_NET');
    } else if (unit === 'PERCENT_FROM_GROSS') {
        return translate('frontend', 'RATE_PERCENT_FROM_GROSS');
    } else if (unit === 'PERCENT_LODGING_NET') {
        return translate('frontend', 'RATE_PERCENT_LODGING_NET');
    } else if (unit === 'PERCENT_LODGING_GROSS') {
        return translate('frontend', 'RATE_PERCENT_LODGING_GROSS');
    } else if (unit === 'PERCENT_PACKPRICE_NET') {
        return translate('frontend', 'RATE_PERCENT_PACKPRICE_NET');
    } else if (unit === 'PERCENT_PACKPRICE_GROSS') {
        return translate('frontend', 'RATE_PERCENT_PACKPRICE_GROSS');
    } else if (unit === 'TIERED_LODGING_GROSS_PER_PERSON_NIGHT') {
        return translate('frontend', 'RATE_TIERED_LODGING_GROSS_PER_PERSON_NIGHT');
    } else if (unit === 'TIERED_LODGING_NET_PER_PERSON_NIGHT') {
        return translate('frontend', 'RATE_TIERED_LODGING_NET_PER_PERSON_NIGHT');
    } else {
        // For forwards compatibility to not crash when new units are added
        return unit;
    }
}

/**
 * @param {number} length
 * @return {string}
 */
export function randomString(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; ++i) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    return result;
}

/**
 * Attempts to open a new tab. If it fails, immediately closes it.
 *
 * @param {Promise<?string>} url
 * @return {Promise<boolean>} Whether a tab was opened or not.
 */
export async function openNewTab(url) {
    // a new tab *must* be opened as a result of user event, otherwise it will be blocked.
    const newWindowReference = window.open();

    let urlString;
    try {
        urlString = typeof url === 'string' ? url : await url;
    } catch (e) {
    }

    if (urlString) {
        newWindowReference.location = urlString;

        return true;
    } else {
        newWindowReference.close();

        // window.close() queues a macro task. Some APIs, like alerts, have to wait until the window is closed.
        return new Promise(resolve => {
            setTimeout(() => resolve(false));
        });
    }
}

export function showFlashMessage(message, typeClass = 'success') {
    const flashMessageElement = document.getElementById('alert-flash-template').content.cloneNode(true);
    flashMessageElement.children[0].classList.add(`alert-${typeClass}`);
    flashMessageElement.children[0].insertAdjacentText('afterbegin', message);

    const $flashMessagesContainer = $('.alert-panel');
    $flashMessagesContainer.finish();
    $flashMessagesContainer.html(flashMessageElement);
    $flashMessagesContainer.show();
    $flashMessagesContainer.fadeOut(4000);
}

/**
 *
 * @param {HTMLElement} element
 * @param {String} text
 * @param {'left'|'right'} position
 */
export function notify(element, text, position = 'left') {
    const boundingBox = element.getBoundingClientRect();

    const notificationElement = document.createElement('span');
    notificationElement.innerText = text;
    notificationElement.classList.add('text-success');
    notificationElement.style.setProperty('position', 'absolute');
    notificationElement.style.setProperty('top', (boundingBox.top + window.scrollY - 10) + 'px');
    notificationElement.style.setProperty('animation-name', 'fadeOutUp');
    notificationElement.style.setProperty('animation-duration', '1.5s');
    notificationElement.style.setProperty('animation-fill-mode', 'both');
    notificationElement.style.setProperty('z-index', '100'); // above modals
    document.body.appendChild(notificationElement);

    let offsetOnLeft = boundingBox.left + window.scrollX;
    if (position === 'right') {
        offsetOnLeft += boundingBox.width - notificationElement.getBoundingClientRect().width;
    }
    notificationElement.style.setProperty('left', offsetOnLeft + 'px');

    setTimeout(() => {
        document.body.removeChild(notificationElement);
    }, 1500);
}

/**
 * @param {HTMLElement} sourceElement
 * @return {HTMLElement[]}
 */
export function getScrollableAncestors(sourceElement) {
    const ancestors = [];
    let element = sourceElement.parentElement;

    while (element && element !== document.body) {
      const style = getComputedStyle(element);
      const overflowX = style.overflowX;
      const overflowY = style.overflowY;

      if (/(auto|scroll|overlay)/.test(overflowX) || /(auto|scroll|overlay)/.test(overflowY)) {
        ancestors.push(element);
      }

      element = element.parentElement;
    }

    return ancestors;
}
